import React from 'react';
import UiSlider from './ui/UiSlider';

export interface AudioStemRowProps {
  role: string;
  volume: number; // 0..1
  muted: boolean;
  solo: boolean;
  level?: number; // 0..1 visual meter (post-fader)
  levelPre?: number; // 0..1 pre-fader meter (background)
  levelDbfs?: number; // instantaneous dBFS value
  attenuationDb?: number; // gain attenuation in dB (<= 0), -Infinity if fully muted
  onVolumeChange: (value: number) => void;
  onToggleMute: () => void;
  onToggleSolo: () => void;
}

export default function AudioStemRow({ role, volume, muted, solo, level = 0, levelPre = 0, levelDbfs, attenuationDb, onVolumeChange, onToggleMute, onToggleSolo }: AudioStemRowProps) {
  return (
  <div className="flex flex-wrap items-center gap-2">
      {/* Left cluster: label + mute/solo + meter + dbfs */}
      <span className="text-xs w-20 shrink-0 text-neutral-300 capitalize truncate" title={role}>{role}</span>
      <button
        type="button"
        onClick={onToggleMute}
        className={`inline-flex items-center justify-center w-5 h-5 p-0 text-neutral-400 hover:text-neutral-200 ${muted ? 'text-rose-500' : ''}`}
        aria-pressed={muted}
        title={muted ? 'Unmute' : 'Mute'}
        aria-label={muted ? 'Unmute' : 'Mute'}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
          <path d="M5 9v6h4l5 4V5L9 9H5z" fill="currentColor" />
          {!muted ? (
            <path d="M16.5 8.5a3.5 3.5 0 010 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          ) : (
            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          )}
        </svg>
      </button>
      <button
        type="button"
        onClick={onToggleSolo}
        className={`inline-flex items-center justify-center w-6 h-6 p-0`}
        aria-pressed={solo}
        title={solo ? 'Disable solo' : 'Solo (Isolate)'}
        aria-label={solo ? 'Disable solo' : 'Solo (Isolate)'}
      >
        <img
          src={solo ? '/icons/headphones-active.svg' : '/icons/headphones.svg'}
          alt={solo ? 'Solo active' : 'Solo'}
          width={16}
          height={16}
        />
      </button>
      {/* Slider + readouts (first row on mobile, right after icons) */}
      <div className="flex items-center gap-2 flex-1 min-w-0 order-1 md:order-none md:w-auto">
        <UiSlider
          value={volume}
          min={0}
          max={1}
          step={0.01}
          onChange={onVolumeChange}
          className="flex-1"
          aria-label={`Volume for ${role}`}
        />
        <span
          className="text-[10px] w-12 text-right text-neutral-400 cursor-pointer hover:text-neutral-200"
          title="Reset level to 0 dB (100%)"
          onClick={() => onVolumeChange(1)}
        >
          {typeof attenuationDb === 'number' ? (isFinite(attenuationDb) ? `${attenuationDb.toFixed(1)} dB` : '-∞ dB') : '\u00A0'}
        </span>
        <span
          className="text-[10px] w-10 text-right text-neutral-400 cursor-pointer hover:text-neutral-200"
          title="Reset level to 100%"
          onClick={() => onVolumeChange(1)}
        >
          {Math.round(volume * 100)}%
        </span>
      </div>

      {/* VU (second row on mobile): meter + dBFS grouped together */}
      <div className="order-2 w-full md:w-auto flex items-center gap-2 md:order-none">
        <div className="relative h-2 flex-1 min-w-0 md:w-24 md:flex-none rounded bg-neutral-800 overflow-hidden">
          <div className="absolute inset-y-0 left-0 bg-neutral-500/40" style={{ width: `${Math.max(0, Math.min(1, levelPre)) * 100}%` }} />
          <div className={`relative h-full ${muted ? 'bg-neutral-400/50' : 'bg-emerald-500'}`} style={{ width: `${Math.max(0, Math.min(1, level)) * 100}%` }} />
        </div>
        <span className="text-[10px] text-neutral-400 w-14 text-right">
          {typeof levelDbfs === 'number' && isFinite(levelDbfs) ? `${levelDbfs.toFixed(1)} dBFS` : '\u00A0'}
        </span>
      </div>
    </div>
  );
}
